import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Box, Button, Icon, IconButton, Menu, MenuButton, MenuItem, MenuList } from "@chakra-ui/react";
import { cloneDeep } from "lodash";
import { useTranslation } from "react-i18next";
import { FaArrowLeft, FaEllipsisV, FaPen, FaPlus, FaTrash } from "react-icons/fa";
import { uniqueId } from "../../../_utils/uniqueId";
import PostEditComment from "./PostEditComment";
import ConfirmDangerButton from "../ConfirmDanger/ConfirmDangerButton";

const emptyComment = {
  message: "",
  delay: 0,
};

const getInitialState = (items, limit) => {
  const newItems = cloneDeep(items?.slice(0, limit) || []);

  return newItems.map((item) => ({ ...item, $id: uniqueId() }));
};

const getItemsToSave = (items) => {
  return items.map(({ $id, ...item }) => item);
};

const PostEditComments = ({
  limit,
  comments,
  onSave,
  onClose,
  messageCounter,
  highlightLinks,
  highlightHashtags,
  highlightAtMentions,
  linkMenuEnabled,
  onToggleLinkDisabling,
  getLinkData,
}) => {
  const { t } = useTranslation(["planner", "common"]);
  const [items, setItems] = useState(getInitialState(comments));
  const [editItemId, setEditItemId] = useState(null);
  const [shouldSave, setShouldSave] = useState(false);

  const addNewItem = useCallback(() => {
    const $id = uniqueId();

    setItems([
      ...items,
      cloneDeep({
        ...emptyComment,
        $id,
      }),
    ]);

    return $id;
  }, [items, setItems]);

  const removeItem = useCallback(($id) => {
    setItems([...items.filter((item) => item.$id !== $id)]);
  },
  [items, setItems]);

  const updateItem = useCallback(($id, data) => {
    setItems([
      ...items.map((item) =>
        item.$id === $id ? { ...item, ...data } : item
      ),
    ]);
  },
  [items, setItems]);

  const handleClose = useCallback(() => onClose?.(), [onClose]);

  const handleItemAdd = useCallback(() => {
    const $id = addNewItem();
    setEditItemId($id);
  }, [addNewItem, setEditItemId]);

  const handleItemCancel = useCallback(($id) => () => {
    // remove item if empty
    if (!items.find((item) => item.$id === $id)?.message) {
      removeItem($id);
    }

    setEditItemId(null);
  }, [setEditItemId, removeItem, items]);

  const handleItemEdit = useCallback(($id) => () => {
    setEditItemId($id);
  }, [setEditItemId]);

  const handleItemSave = useCallback(($id) => (data) => {
    updateItem($id, data);
    setEditItemId(null);
    setShouldSave(true);
  }, [updateItem, setEditItemId, setShouldSave]);

  const handleItemRemove = useCallback(($id) => () => {
    removeItem($id);
    setEditItemId(null);
    setShouldSave(true);
  }, [removeItem, setEditItemId, setShouldSave]);

  useEffect(() => {
    if (shouldSave) {
      onSave?.(getItemsToSave(items));
      setShouldSave(false);
    }
  }, [shouldSave]);

  return (
    <Box>
      <Box display="flex" flexDirection="column" minHeight="8rem">
        {items.length ? (
          <>
            {items.map((item, index) => (
              <Box key={item.$id} marginBottom="3">
                {editItemId && editItemId === item.$id ? (
                  <PostEditComment
                    data={item}
                    minDelay={index > 0 ? 1 : 0}
                    maxDelay={120}
                    onCancel={handleItemCancel(item.$id)}
                    onSave={handleItemSave(item.$id)}
                    messageCounter={messageCounter}
                    highlightLinks={highlightLinks}
                    highlightHashtags={highlightHashtags}
                    highlightAtMentions={highlightAtMentions}
                    linkMenuEnabled={linkMenuEnabled}
                    onToggleLinkDisabling={onToggleLinkDisabling}
                    getLinkData={getLinkData}
                  />
                ) : (
                  <Box
                    display="flex"
                    alignItems="flex-start"
                    background="brand.glass_light_10"
                    borderRadius="lg"
                    paddingX="3"
                    paddingY="2"
                  >
                    <Box display="flex" flexDirection="column" flex="1">
                      <Box fontSize="sm" color="gray.300">{`${item.delay || 0} ${t("post_edit_comments.delay.suffix")}`}</Box>
                      <Box fontSize="sm" color="gray.100">{item.message}</Box>
                    </Box>
                    <Box marginLeft="2">
                      <Menu placement="bottom-end">
                        <MenuButton
                          variant="ghost"
                          size="sm"
                          as={IconButton}
                          icon={<Icon as={FaEllipsisV} />}
                        />
                        <MenuList fontSize="sm" minWidth="auto">
                          <MenuItem
                            icon={<Icon fontSize="sm" as={FaPen} />}
                            onClick={handleItemEdit(item.$id)}
                          >
                            {t("edit", { ns: "common" })}
                          </MenuItem>
                          <ConfirmDangerButton
                            as={MenuItem}
                            icon={<Icon fontSize="sm" as={FaTrash} />}
                            confirmTitle={t(
                              "post_edit_comments.action.delete_confirmation_title"
                            )}
                            confirmMessage={t(
                              "post_edit_comments.action.delete_confirmation"
                            )}
                            confirmButtonText={t("post_edit_comments.action.delete")}
                            onConfirm={handleItemRemove(item.$id)}
                          >
                            {t("delete", { ns: "common" })}
                          </ConfirmDangerButton>
                        </MenuList>
                      </Menu>
                    </Box>
                  </Box>
                )}
              </Box>
            ))}
          </>
        ) : (
          <Box
            fontSize="sm"
            color="gray.300"
            textAlign="center"
            paddingY="2"
            marginY="auto"
          >
            {t("post_edit_comments.no_items")}
          </Box>
        )}
      </Box>
      {!editItemId && (
        <Box display="flex" paddingY="2">
          <Button
            size="sm"
            variant="secondary"
            leftIcon={<Icon as={FaArrowLeft} />}
            onClick={handleClose}
          >
            {t("back", { ns: "common" })}
          </Button>
          <Button
            size="sm"
            variant="solid"
            leftIcon={<Icon as={FaPlus} />}
            disabled={items.length >= limit}
            onClick={handleItemAdd}
            marginLeft="auto"
          >
            {t("post_edit_comments.action.add_next")}
          </Button>
        </Box>
      )}
    </Box>
  );
};

PostEditComments.propTypes = {
  limit: PropTypes.number,
  comments: PropTypes.arrayOf(
    PropTypes.shape({
      message: PropTypes.string.isRequired,
      delay: PropTypes.number,
    })
  ),
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  messageCounter: PropTypes.func,
  highlightLinks: PropTypes.bool,
  highlightHashtags: PropTypes.bool,
  highlightAtMentions: PropTypes.bool,
  linkMenuEnabled: PropTypes.bool,
  onToggleLinkDisabling: PropTypes.func,
  getLinkData: PropTypes.func,
};

PostEditComments.defaultProps = {
  limit: undefined,
  comments: undefined,
  messageCounter: undefined,
  highlightLinks: undefined,
  highlightHashtags: undefined,
  highlightAtMentions: undefined,
  linkMenuEnabled: undefined,
  onToggleLinkDisabling: undefined,
  getLinkData: undefined,
};

export default PostEditComments;
